import { useMemo } from "react";
import produce from "immer";
import { head, keys } from "lodash";
import { AbstractDate } from "@linkfluence/models/date";
import { Timezone } from "@linkfluence/models/timezone";
import {
  getIntervalFromDates,
  getLastYearPeriod,
  getPreviousPeriod,
} from "@tootools/date";
import { useCurrentProjectFocusesId } from "@radarly/core/project/use-cases";
import { getFragmentWithContext } from "@radarly/models/cards/fragment";
import { TimeUnit } from "@radarly/models/cards/types/time-unit";
import { Clause } from "@radarly/models/clause";
import { getDateFromClause } from "@radarly/models/clause/getter";
import { mergeClauses } from "@radarly/models/clause/merge";
import { refineClauseWithDate } from "@radarly/models/clause/refiner";
import { getComparaisonPeriodFromFragments } from "@radarly/models/fragment";
import { useIsCurrentInsightPageGlobal } from "@radarly/models/insight-pages/is";
import {
  AIFragment,
  SearchFragment,
  isSearchFragmentDateMetrics,
  isSearchFragmentDateTerm,
} from "@radarly/models/search";

function getPreviousPeriodFromComparaison(
  comparaison: "previous_period" | "last_year",
  from: AbstractDate,
  to: AbstractDate
) {
  if (from && to && comparaison) {
    return comparaison === "previous_period"
      ? getPreviousPeriod({ from, to })
      : getLastYearPeriod({ from, to });
  } else {
    return {
      from: null,
      to: null,
    };
  }
}

export function updateClauseWithDates(
  clause: Clause,
  from?: AbstractDate,
  to?: AbstractDate
) {
  const { from: fromClause, to: toClause, tz } = getDateFromClause(clause);

  const dateClause = {
    from: from || fromClause,
    to: to || toClause,
    tz,
  };

  return refineClauseWithDate(clause, dateClause);
}

export function getUpdatedFragment(
  fragment: SearchFragment | AIFragment,
  clause: Clause,
  from: AbstractDate,
  to: AbstractDate,
  timezone: Timezone,
  projectFocuses: number[],
  isGlobalPage: boolean,
  timeUnit: TimeUnit
) {
  if (fragment) {
    const validFragment = isGlobalPage
      ? getFragmentWithContext(fragment, undefined, projectFocuses)
      : fragment;

    return produce(validFragment, (draft) => {
      draft.request.query = updateClauseWithDates(clause, from, to);
      if (
        isSearchFragmentDateTerm(draft) ||
        isSearchFragmentDateMetrics(draft)
      ) {
        const { from: fromClause, to: toClause } = getDateFromClause(
          draft.request.query
        );
        draft.request.aggs.date.interval =
          !!timeUnit && timeUnit !== "default"
            ? timeUnit
            : getIntervalFromDates(fromClause, toClause, "UTC");
      }
      // @TODO REMOVE AFTER MIGRATION
      // @ts-ignore
      if (draft.request.aggs) {
        // @ts-ignore
        const term = head(keys(draft.request.aggs));

        if (term) {
          // @ts-ignore
          const metric = head(draft.request.aggs[term]?.metrics);
          if (metric) {
            // @ts-ignore
            draft.request.aggs.metrics = { metrics: [metric] };
          }
        }
      }
      draft.request.timezone = timezone;
    });
  } else {
    return null;
  }
}

export function useFragment<F extends SearchFragment | AIFragment>(
  fragment: F,
  clause: Clause,
  from: AbstractDate,
  to: AbstractDate,
  timezone: Timezone,
  timeUnit?: TimeUnit,
  enabled: boolean = true
): F {
  const isGlobalPage = useIsCurrentInsightPageGlobal();
  const projectFocuses = useCurrentProjectFocusesId();
  const nextClause = useMemo(() => {
    return mergeClauses(
      fragment?.request?.query ? fragment.request.query : { and: [] },
      clause
    );
  }, [clause, fragment]);
  return useMemo(() => {
    return enabled
      ? (getUpdatedFragment(
          fragment,
          nextClause,
          from,
          to,
          timezone,
          projectFocuses,
          isGlobalPage,
          timeUnit
        ) as F)
      : (fragment as F);
  }, [
    fragment,
    from,
    nextClause,
    projectFocuses,
    timezone,
    to,
    isGlobalPage,
    timeUnit,
    enabled,
  ]);
}

export function useFragments<F extends SearchFragment | AIFragment>(
  fragments: [F, F],
  clause: Clause,
  from: AbstractDate,
  to: AbstractDate,
  timezone: Timezone,
  timeUnit?: TimeUnit
): [F, F] | [F] {
  const fragment = useFragment(
    fragments[0],
    clause,
    from,
    to,
    timezone,
    timeUnit
  );
  const comparaison = getComparaisonPeriodFromFragments(
    fragments[0],
    fragments[1] || fragments[0]
  );
  const previousPeriod = getPreviousPeriodFromComparaison(
    comparaison,
    from,
    to
  );
  const secondFragment = useFragment(
    fragments[1],
    clause,
    previousPeriod.from,
    previousPeriod.to,
    timezone,
    timeUnit
  );
  if (secondFragment) {
    return [fragment, secondFragment];
  } else {
    return [fragment];
  }
}
